import React from 'react';

import { EditListButton } from '@vlabs/monokit/features/edit-list';
import { useGetList } from '@vlabs/api/luna-api/client';
import type { ListId } from '@vlabs/api/luna-api/model';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { useTranslation } from 'react-i18next';

import st from './ListGeneralInfo.module.sass';

export interface ListGeneralInfoProps {
  listId: ListId;
}

export function ListGeneralInfo({ listId }: ListGeneralInfoProps): React.JSX.Element {
  const { t } = useTranslation();
  const getList = useGetList(listId);

  const listName = getList.data?.user_data;
  const parsedCreateTime = getList.data?.create_time ? parseISOToLocale(getList.data?.create_time) : '-';
  const parsedLastUpdateTime = getList.data?.last_update_time ? parseISOToLocale(getList.data?.last_update_time) : '-';

  return (
    <div
      className={st.Info}
      data-testid="listGeneralInfo"
    >
      <header className={st.InfoHeader}>
        <h6>{t('list-details-page:info block.title')}</h6>
        <EditListButton
          kind="secondary"
          listId={listId}
          size="small"
          variant="filled"
        />
      </header>
      <div className={st.InfoBody}>
        <div className={st.Row}>
          <span
            className={st.Label}
            data-testid="listGeneralInfo.listName.label"
          >
            {t('list-details-page:info block.name')}
          </span>
          <span
            className={st.Value}
            data-testid="listGeneralInfo.listName.value"
          >
            <TruncatedText
              hasTruncation
              tooltipContent={getList.data?.user_data}
            >
              {listName ?? '-'}
            </TruncatedText>
          </span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span
            className={st.Label}
            data-testid="listGeneralInfo.created.label"
          >
            {t('list-details-page:info block.created')}
          </span>
          <span
            className={st.Value}
            data-testid="listGeneralInfo.created.value"
          >
            {parsedCreateTime}
          </span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span
            className={st.Label}
            data-testid="listGeneralInfo.lastChanges.label"
          >
            {t('list-details-page:info block.last changes')}
          </span>
          <span
            className={st.Value}
            data-testid="listGeneralInfo.lastChanges.value"
          >
            {parsedLastUpdateTime}
          </span>
        </div>
        <div className={st.Divider} />
      </div>
    </div>
  );
}
